<c-container fluid class="p-4">
  <c-row>
    <c-col xs="12">
      <!-- Info Cards -->
      @if (payoutInfo(); as info) {
        <c-row class="mb-4">
          <c-col xs="12" md="4">
            <c-card class="info-card">
              <c-card-body class="text-center">
                <div class="text-muted mb-1">Available for Payout</div>
                <div class="h3 mb-0 text-success">{{ formatAmount(info.balance) }} USD</div>
                @if (info.pending_payout_amount > 0) {
                  <small class="text-muted">
                    ({{ formatAmount(info.total_balance) }} - {{ formatAmount(info.pending_payout_amount) }} pending)
                  </small>
                }
              </c-card-body>
            </c-card>
          </c-col>

          <c-col xs="12" md="4">
            <c-card class="info-card">
              <c-card-body class="text-center">
                <div class="text-muted mb-1">Pending Payouts</div>
                <div class="h3 mb-0" [class.text-warning]="info.pending_payout_amount > 0" [class.text-muted]="info.pending_payout_amount === 0">
                  {{ formatAmount(info.pending_payout_amount) }} USD
                </div>
              </c-card-body>
            </c-card>
          </c-col>

          <c-col xs="12" md="4">
            <c-card class="info-card">
              <c-card-body class="text-center">
                <div class="text-muted mb-1">Payout Fee</div>
                <div class="h4 mb-0 text-warning">{{ info.fee_formula }}</div>
              </c-card-body>
            </c-card>
          </c-col>
        </c-row>
      }

      <c-card class="border-0">
        <c-card-body class="p-0">
          <!-- Messages -->
          @if (error(); as err) {
            <c-alert color="danger" [dismissible]="true" (visibleChange)="error.set(null)">
              {{ err }}
            </c-alert>
          }
          @if (success(); as msg) {
            <c-alert color="success" [dismissible]="true" (visibleChange)="success.set(null)">
              {{ msg }}
            </c-alert>
          }

          <!-- Filters -->
          <c-row class="mb-3 justify-content-start align-items-end">
            <c-col md="3" class="mb-4 mb-md-0">
              <label cLabel for="selectStatus">Status</label>
              <select
                id="selectStatus"
                cSelect
                [ngModel]="filters().filter_status ?? ''"
                (ngModelChange)="updateFilterProperty('filter_status', $event)"
              >
                <option value="">All</option>
                @for (status of statusOptions(); track status) {
                  <option [value]="status">{{ status }}</option>
                }
              </select>
            </c-col>

            <c-col md="2" class="mb-4 mb-md-0">
              <label cLabel for="selectPageCount">Per Page</label>
              <select
                id="selectPageCount"
                cSelect
                [ngModel]="filters().per_page"
                (ngModelChange)="updateFilterProperty('per_page', +$event)"
              >
                @for (option of perPageOptions; track option) {
                  <option [value]="option">{{ option }}</option>
                }
              </select>
            </c-col>

            <c-col md="auto" class="mb-4 mb-md-0">
              <button cButton color="secondary" (click)="resetFilters()" class="w-100 w-md-0">Reset</button>
            </c-col>
          </c-row>
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>

  <c-row>
    <c-col>
      <c-card>
        <c-card-header class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">My Payouts</h6>
          <button cButton color="primary" (click)="navigateToNewPayout()">
            <svg cIcon name="cilPlus" class="me-1"></svg>
            New Payout Request
          </button>
        </c-card-header>

        <c-card-body>
          <!-- Loading -->
          @if (loading()) {
            <div class="text-center py-4">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
          }

          <!-- Table -->
          @if (!loading()) {
            @if (payouts().length > 0) {
              <div class="table-responsive">
                <table cTable [hover]="true" [striped]="true">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Status</th>
                      <th class="text-end">Amount</th>
                      <th class="text-end">Fee</th>
                      <th class="text-end">You Receive</th>
                      <th>Wallet</th>
                      <th>Requested</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (payout of payouts(); track payout.id) {
                      <tr>
                        <td>#{{ payout.id }}</td>
                        <td>
                          <c-badge [color]="getStatusBadgeClass(payout.status)">
                            {{ payout.status }}
                          </c-badge>
                        </td>
                        <td class="text-end">{{ formatAmount(payout.amount) }}</td>
                        <td class="text-end text-muted">{{ formatAmount(payout.fee) }}</td>
                        <td class="text-end text-success">{{ formatAmount(payout.amount_sent) }}</td>
                        <td class="text-muted">{{ payout.web3_target_wallet_display }}</td>
                        <td>{{ formatDate(payout.requested_at) }}</td>
                        <td>
                          <button cButton size="sm" color="info" class="me-1" (click)="openDetailModal(payout)">
                            Details
                          </button>
                          @if (payout.status === 'requested') {
                            <button cButton size="sm" color="danger" (click)="cancelPayout(payout)">
                              Cancel
                            </button>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                  <tfoot class="table-dark">
                    <tr>
                      <td colspan="2" class="text-end"><strong>Totals:</strong></td>
                      <td class="text-end"><strong>{{ formatAmount(totalAmount()) }}</strong></td>
                      <td class="text-end"><strong>{{ formatAmount(totalFee()) }}</strong></td>
                      <td class="text-end"><strong>{{ formatAmount(totalAmount() - totalFee()) }}</strong></td>
                      <td colspan="3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            } @else {
              <div class="text-center py-4">
                <p class="text-muted mb-3">No payout requests yet.</p>
                <button cButton color="primary" (click)="navigateToNewPayout()">
                  Request your first payout
                </button>
              </div>
            }

            <!-- Pagination -->
            @if (totalPages() > 1) {
              <div class="mt-3 d-flex justify-content-between align-items-center">
                <c-pagination aria-label="Payouts pagination" class="mb-0">
                  <li cPageItem [disabled]="filters().page === 1" (click)="changePage(1)">
                    <a cPageLink>First</a>
                  </li>
                  <li cPageItem [disabled]="filters().page === 1" (click)="changePage((filters().page ?? 1) - 1)">
                    <a cPageLink>Prev</a>
                  </li>
                  @for (page of visiblePages(); track page) {
                    <li cPageItem [active]="page === filters().page" (click)="changePage(page)">
                      <a cPageLink>{{ page }}</a>
                    </li>
                  }
                  <li cPageItem [disabled]="filters().page === totalPages()" (click)="changePage((filters().page ?? 1) + 1)">
                    <a cPageLink>Next</a>
                  </li>
                  <li cPageItem [disabled]="filters().page === totalPages()" (click)="changePage(totalPages())">
                    <a cPageLink>Last</a>
                  </li>
                </c-pagination>

                <span class="text-muted">
                  {{ showingStart() }} - {{ showingEnd() }} of {{ totalCount() }}
                </span>
              </div>
            }
          }
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>

  <!-- Detail Modal -->
  <c-modal [visible]="showDetailModal()" (visibleChange)="showDetailModal.set($event)" size="lg">
    <c-modal-header>
      <h5 cModalTitle>Payout Details #{{ selectedPayout()?.id }}</h5>
      <button cButtonClose (click)="closeDetailModal()"></button>
    </c-modal-header>
    <c-modal-body>
      @if (loadingDetail()) {
        <div class="text-center py-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      }

      @if (!loadingDetail() && selectedPayout(); as payout) {
        <c-row class="mb-3">
          <c-col xs="6">
            <div class="text-muted">Status</div>
            <c-badge [color]="getStatusBadgeClass(payout.status)" class="fs-6">
              {{ payout.status }}
            </c-badge>
          </c-col>
          <c-col xs="6">
            <div class="text-muted">Requested</div>
            <div>{{ formatDate(payout.requested_at) }}</div>
          </c-col>
        </c-row>

        <c-row class="mb-3">
          <c-col xs="4">
            <div class="text-muted">Amount</div>
            <div class="h5">{{ formatAmount(payout.amount) }} USD</div>
          </c-col>
          <c-col xs="4">
            <div class="text-muted">Fee</div>
            <div class="h5 text-warning">{{ formatAmount(payout.fee) }} USD</div>
          </c-col>
          <c-col xs="4">
            <div class="text-muted">You Receive</div>
            <div class="h5 text-success">{{ formatAmount(payout.amount_sent) }} USD</div>
          </c-col>
        </c-row>

        <c-row class="mb-3">
          <c-col xs="12">
            <div class="text-muted">Target Wallet</div>
            <div class="text-break">{{ payout.web3_target_wallet }}</div>
          </c-col>
        </c-row>

        @if (payout.subject) {
          <c-row class="mb-3">
            <c-col xs="12">
              <div class="text-muted">Subject</div>
              <div>{{ payout.subject }}</div>
            </c-col>
          </c-row>
        }

        <!-- Transaction Details (if completed/pending) -->
        @if (payout.web3_tx_hash) {
          <div class="mt-4 p-3 bg-dark rounded">
            <h6 class="mb-3">Transaction Details</h6>
            <c-row class="mb-2">
              <c-col xs="12">
                <div class="text-muted">Transaction Hash</div>
                <div class="text-break">
                  <a [href]="'https://bscscan.com/tx/' + payout.web3_tx_hash" target="_blank" class="text-primary">
                    {{ payout.web3_tx_hash }}
                  </a>
                </div>
              </c-col>
            </c-row>
            @if (payout.web3_confirmations !== undefined) {
              <c-row>
                <c-col xs="6">
                  <div class="text-muted">Confirmations</div>
                  <div>{{ payout.web3_confirmations }}</div>
                </c-col>
                @if (payout.web3_block_number) {
                  <c-col xs="6">
                    <div class="text-muted">Block Number</div>
                    <div>{{ payout.web3_block_number }}</div>
                  </c-col>
                }
              </c-row>
            }
          </div>
        }

        <!-- Completed Date -->
        @if (payout.completed_at) {
          <c-row class="mt-3">
            <c-col xs="12">
              <div class="text-muted">Completed</div>
              <div class="text-success">{{ formatDate(payout.completed_at) }}</div>
            </c-col>
          </c-row>
        }
      }
    </c-modal-body>
    <c-modal-footer>
      @if (selectedPayout()?.status === 'requested') {
        <button cButton color="danger" class="me-2" (click)="cancelPayout(selectedPayout()!)">
          Cancel Request
        </button>
      }
      <button cButton color="secondary" (click)="closeDetailModal()">Close</button>
    </c-modal-footer>
  </c-modal>
</c-container>